import { useFormik, FormikProvider, getIn } from "formik";
import * as Yup from "yup";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { IRenter } from "../../../../types/renter.type";
import InputFormik from "../../../../common/components/form/InputFormik";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import AddressFields from "./fields/AddressFields";
import ReferenceFields from "./fields/ReferenceFields";
import { isCPF } from "../../../../common/helpers/is-cpf";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  maskCNH,
  maskCPF,
  maskPhone,
} from "../../../../common/helpers/mask.helpers";
import { DocumentRow } from "./fields/DocumentRow";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import TextareaFormik from "./TextareaFormik";
import { useCompanyStore } from "../../../../common/states/store/Company";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { getSdrOptions } from "../../../../common/helpers/get-sdr";
import { ConsultantOption } from "@/types/employee.type";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";

// Esquema de validação com Yup
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .test(
      "full-name",
      "Deve incluir pelo menos um nome e um sobrenome",
      (value) => {
        if (!value) return false;
        const parts = value.split(" ");
        return parts.length >= 2;
      }
    ),
  email: Yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  whatsapp: Yup.string().required("WhatsApp é obrigatório"),
  gender: Yup.string()
    .oneOf(["male", "female", "other"], "Gênero inválido")
    .required("Gênero é obrigatório"),
  birthDate: Yup.date()
    .required("Data de nascimento é obrigatória")
    .max(new Date(), "Data de nascimento não pode ser no futuro"),
  cnh: Yup.string().required("CNH é obrigatória"),
  //.test('valida-cnh', 'CNH inválida', value => isCNH(value)),
  categoryCNH: Yup.string().required("Tipo de CNH é obrigatório"),
  cpf: Yup.string()
    .required("CPF é obrigatório")
    .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  maritalStatus: Yup.string().required("Selecione estado civil"),
  address: Yup.object().shape({
    zipcode: Yup.string().required("CEP é obrigatório"),
    street: Yup.string().required("Rua é obrigatória"),
    number: Yup.string().required("Número é obrigatório"),
    complement: Yup.string(),
    state: Yup.string().required("Estado é obrigatório"),
    city: Yup.string().required("Cidade é obrigatória"),
    district: Yup.string().required("Bairro é obrigatório"),
  }),

  references: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Nome da referência é obrigatório"),
      content: Yup.string().required("Telefone da referência é obrigatório"),
      verified: Yup.boolean().default(false),
    })
  ),
  documents: Yup.array().of(
    Yup.object().shape({
      file: Yup.mixed().default(undefined),
      type: Yup.string().default(""),
      content: Yup.string().default(""),
      verified: Yup.boolean().default(false),
    })
  ),
  cnhExpirationDate: Yup.date().required("Data de expiração é obrigatório"),
  typeSanguine: Yup.string().required("Tipo sanguíneo é obrigatório"),
  rg: Yup.string().required("Número do RG é obrigatório"),
  rg_orgexp: Yup.string().required("Orgão expedidor é obrigatório"),
  motherName: Yup.string().required("Nome da mãe é obrigatório"),
  technicalConsultant: Yup.string().required("Consultor é obrigatório"),
  cpfDoIndicador: Yup.string().when("cpfDoIndicadorOptions.indicated", {
    is: true,
    then: (schema) =>
      schema
        .required("CPF do Indicador é obrigatório")
        .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  }),
});

type FormPropsCreateRenter = {
  onSubmit: (
    values: IRenter
  ) => Promise<{ ok: boolean; data: IRenter; errors?: unknown[] }>;
  data: IRenter;
  formRequestErrors?: { message: string; path: string[] }[];
};

function FormEditPersonalData(props: FormPropsCreateRenter) {
  const authCtx = useContext(AuthContext);
  const userEmail = authCtx.auth?.user?.email;
  const { filterMode, selectedCompanies, selectedRegion, selectedUf } =
    useCompanyStore();

  const { notifySuccess } = useToastContext();
  const [theInitialData, setNewInitialData] = useState<IRenter>(
    Object.assign(props.data, {
      birthDate: (props.data.birthDate || "").split("T")[0],
      cnhExpirationDate: (props.data.cnhExpirationDate || "").split("T")[0],
      documents: props.data.documents.map((doc) =>
        Object.assign(doc, { file: undefined })
      ),
      isBlacklisted: props.data?.isBlacklisted || false,
      criminalCheck: {
        jus: props.data?.criminalCheck?.jus || false,
        escavador: props.data?.criminalCheck?.escavador || false,
      },
      cpfDoIndicadorOptions: {
        indicated: props?.data?.cpfDoIndicadorOptions?.indicated || false,
        notIndicated: props?.data?.cpfDoIndicadorOptions?.notIndicated || false,
      },
      jurValidation: {
        status: props?.data?.jurValidation?.status || false,
        validated: props?.data?.jurValidation?.validated || false,
        details: props?.data?.jurValidation?.details || "",
      },
      observations: props.data.observations || [
        { text: "", date: new Date().toISOString(), author: "" },
      ],
      groupName: (() => {
        try {
          const selectedCompanies = localStorage?.selectedCompanies;
          if (!selectedCompanies) return "";
          const parsed = JSON.parse(selectedCompanies);
          return Array.isArray(parsed) && parsed.length > 0 ? parsed[0]?.city || "" : "";
        } catch {
          return "";
        }
      })(),
    })
  );
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    onSubmit: async (data: IRenter) => {
      data.observations.forEach((observation) => {
        if (!observation.date) {
          observation.date = new Date().toISOString();
        }

        if (!observation.author) {
          // Only set the author if it hasn't been set yet
          observation.author = userEmail || "";
        }
      });

      const result = await props.onSubmit(data);
      if (result.ok) {
        formik.setValues(result.data);
        setNewInitialData(result.data);
        notifySuccess("Atualizado com sucesso.");
      }
      return result;
    },
  });

  const observations = getIn(formik.values, "observations");
  const currentBirthDate = getIn(formik.values, "birthDate");
  const currentObservation = useMemo(() => {
    const total = observations.filter(({ date }) => !!date);
    return total.length;
  }, [observations]);
  const age = useMemo(() => {
    if (currentBirthDate) {
      const birthDate = new Date(currentBirthDate);
      const today = new Date();

      let calculatedAge = today.getFullYear() - birthDate.getFullYear();

      // Se o mês atual for menor que o mês de nascimento ou for o mesmo mês mas o dia ainda não ocorreu, subtrai 1 da idade
      if (
        today.getMonth() < birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() &&
          today.getDate() < birthDate.getDate())
      ) {
        calculatedAge--;
      }
      return calculatedAge;
    }

    return null;
  }, [currentBirthDate]);

  const [, setCurrentDate] = useState(new Date().toISOString().split("T")[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date().toISOString().split("T")[0]);
    }, 60000); // Atualiza a cada minuto

    return () => clearInterval(interval); // Limpa o intervalo quando o componente é desmontado
  }, []);

  // const invalidCpf = getIn(formik.errors, 'cpfDoIndicador');
  useEffect(() => {
    const fetchConsultants = async () => {
      let companyFilter: string[] | undefined;
      let ufFilter: string | null = null;

      if (filterMode === "cities" && selectedCompanies.length > 0) {
        companyFilter = selectedCompanies.map((c) => c.companyId);
      } else if (filterMode === "uf") {
        ufFilter = selectedUf;
      }

      const options = await getConsultantOptionsFromAPI(
        companyFilter,
        ufFilter || undefined
      );

      // Lógica para garantir que o valor salvo seja mantido na lista
      const savedValue = props.data.technicalConsultant;
      if (savedValue && !options.some((opt) => opt.value === savedValue)) {
        options.unshift({ value: savedValue, label: savedValue });
      }

      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [
    filterMode,
    selectedCompanies,
    selectedRegion,
    selectedUf,
    props.data.technicalConsultant,
  ]);
  const savedSDR = formik.values.sdr;

  const sdrOptions = getSdrOptions(savedSDR);

  return (
    <form className="form-edit" onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="section-name">Informações Pessoais</h2>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Primeira linha */}
            <div className="style-informacoes-pessoais">
              <InputFormik
                label="Nome completo:"
                name="name"
                className=""
                required={true}
              />
              <InputFormik
                label="E-mail:"
                name="email"
                type="email"
                required={true}
              />
              <InputFormik
                label="WhatsApp:"
                name="whatsapp"
                type="whatsapp"
                required={true}
                mask={maskPhone}
              />

              <ComboBoxFormik
                label="Gênero:"
                name="gender"
                options={[
                  { value: "", label: "-" },
                  { value: "male", label: "Masculino" },
                  { value: "female", label: "Feminino" },
                  { value: "other", label: "Outro" },
                ]}
                required={true}
              />

              <InputFormik
                label="Nome da mãe:"
                name="motherName"
                required={true}
              />

              <InputFormik
                label={`Data de nascimento (${age} anos):`}
                name="birthDate"
                type="date"
                required={true}
              />

              <ComboBoxFormik
                label="Estado civil:"
                name="maritalStatus"
                options={[
                  { value: "", label: "-" },
                  { value: "Solteiro", label: "Solteiro" },
                  { value: "Casado", label: "Casado" },
                  { value: "Separado", label: "Separado" },
                  { value: "Divorciado", label: "Divorciado" },
                  { value: "Viúvo", label: "Viúvo" },
                  { value: "Amasiado", label: "Amasiado" },
                ]}
                required={true}
              />

              <InputFormik
                label="CPF:"
                name="cpf"
                placeholder="111.222.333-44"
                required={true}
                mask={maskCPF}
              />

              <ComboBoxFormik
                label="Tipo sanguíneo:"
                name="typeSanguine"
                options={[
                  { value: "-", label: "-" },
                  { value: "O-", label: "O-" },
                  { value: "O+", label: "O+" },
                  { value: "A-", label: "A-" },
                  { value: "A+", label: "A+" },
                  { value: "B-", label: "B-" },
                  { value: "B+", label: "B+" },
                  { value: "AB-", label: "AB-" },
                  { value: "AB+", label: "AB+" },
                ]}
                required={true}
              />

              <InputFormik
                label="CNH:"
                name="cnh"
                placeholder="00000000"
                required={true}
                mask={maskCNH}
              />

              <InputFormik
                label="Data de vencimento da CNH:"
                name="cnhExpirationDate"
                type="date"
                required={true}
              />

              <ComboBoxFormik
                label="Categoria CNH:"
                name="categoryCNH"
                options={[
                  { value: "-", label: "-" },
                  { value: "A", label: "A" },
                  { value: "B", label: "B" },
                ]}
                required={true}
              />

              <InputFormik label="RG:" name="rg" required={true} />

              <InputFormik
                label="Orgão expedidor:"
                name="rg_orgexp"
                required={true}
              />
              <ComboBoxFormik
                label="Origem do Lead:"
                name="leadOrigin"
                options={[
                  { value: "-", label: "-" },
                  { value: "Instagram", label: "Instagram" },
                  { value: "Facebook", label: "Facebook" },
                  { value: "Google", label: "Google" },
                  { value: "Olx", label: "Olx" },
                  { value: "Rádio", label: "Rádio" },
                  { value: "Televisão", label: "Televisão" },
                  {
                    value: "Indicação de Alguém",
                    label: "Indicação de alguém",
                  },
                  { value: "Renner Cbzona", label: "Renner Cbzona" },
                  { value: "Beto Brasil", label: "Beto Brasil" },
                  { value: "Site", label: "Site www.locavibe.com.br" },
                  { value: "Embaixador", label: "Embaixador" },
                  { value: "TikTok", label: "TikTok" },
                  { value: "SDR", label: "SDR" },
                  { value: "Unid. Móvel", label: "Unid. Móvel" },
                ]}
              />

              <ComboBoxFormik
                label="Consultor:"
                name="technicalConsultant"
                options={consultantOptions}
                required={true}
                disabled={formik.values.technicalConsultant !== ""}
              />
              <ComboBoxFormik
                label="Representante de vendas (SDR):"
                name="sdr"
                options={sdrOptions}
                required={true}
                disabled={formik.values.sdr !== ""}
              />
              <div className="flex items-center whitespace-nowrap">
                <input
                  type="checkbox"
                  id="isBlacklisted"
                  name="isBlacklisted"
                  checked={formik.values.isBlacklisted || false}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-6 w-6 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                />
                <label
                  htmlFor="isBlacklisted"
                  className="ml-3 font-medium text-gray-700 select-none text-lg"
                >
                  Cliente na Blacklist
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* <section>
          <CpfDoIndicadorCheck invalidCpf={invalidCpf} />
        </section> */}

        <section className="style-endereco">
          <h2 className="section-name">Endereço</h2>
          <AddressFields />
        </section>

        <section>
          <h2 className="section-name">Referências</h2>
          {theInitialData.references.map((ref, index) => (
            <ReferenceFields key={index} index={index} reference={ref} />
          ))}
        </section>

        <section className="mt-5 mb-4 p-3">
          <h2 className="section-name">Documentos</h2>

          <div className="flex flex-col items-stretch">
            {formik.values?.documents?.map((doc, index) => (
              <div
                key={index}
                className={`flex flex-col space-y-2 p-3 my-0 rounded ${index % 2 ? "bg-gray/5" : "bg-gray/25"
                  }`}
              >
                <DocumentRow
                  name={`documents.${index}`}
                  initialData={doc || {}}
                />
              </div>
            ))}
            <button
              type="button"
              className="button-adicionar-documento"
              onClick={() => {
                formik.setFieldValue(
                  `documents.${formik.values.documents.length}`,
                  { type: "", content: "", verified: false }
                );
              }}
            >
              + Adicionar documento
            </button>
          </div>
        </section>

        <section>
          <div className="style-observacoes">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <div>
              <TextareaFormik
                label={`Observação`}
                name={`observations.${currentObservation}.text`}
                className="min-h-64 w-full rounded shadow-md focus:border-white"
              />
            </div>
          </div>
        </section>

        <div className="w-full flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditPersonalData;
